import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
} from "@mui/material";
import { useCustomTranslation } from "../../../hooks/useCustomTranslation";
import { useApiClientQuery } from "../../../hooks/useApiClient";
import api_endpoints from "../../../api";

export default function ApplyPaymentPlanModal({
  orgId,
  onApply,
  onOpen,
  onClose,
}) {
  const { t } = useCustomTranslation("Recurring Payment");

  // ---- Mock fallback (used if API not available) ----
  const mockPlans = useMemo(
    () => [
      { id: "p1", name: t("starter"), amount: 10, frequency: "Monthly", taxable: false },
      { id: "p2", name: t("supporter"), amount: 25, frequency: "Monthly", taxable: true },
      { id: "p3", name: t("patron"), amount: 50, frequency: "Monthly", taxable: false },
      { id: "p4", name: t("annual_gift"), amount: 100, frequency: "Yearly", taxable: true },
      { id: "p5", name: t("quarter_backer"), amount: 30, frequency: "Quarterly", taxable: false },
    ],
    [t]
  );

  // ---- Load real plans (quietly; no retries/refresh spam) ----
  const plansQuery = useApiClientQuery(
    () => api_endpoints.listRecurringPlans(orgId, { active: true, limit: 200 }),
    {
      queryKey: ["recurring-plans", orgId, { active: true }],
      enabled: Boolean(orgId),
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    }
  );

  const apiPlans = Array.isArray(plansQuery.data) ? plansQuery.data : null;

  // normalize API → UI shape
  const normalizedPlans = useMemo(() => {
    if (!apiPlans) return null;
    return apiPlans.map((p) => ({
      id: p._id || p.id,
      name: p.name,
      amount: Number(p.amount),
      frequency: p.frequency, // "Weekly" | "Monthly" | ...
      taxable: Boolean(p.taxable),
      description: p.description || "",
    }));
  }, [apiPlans]);

  const sourcePlans = normalizedPlans ?? mockPlans;

  // ---- search / filter ----
  const [query, setQuery] = useState("");

  const plans = useMemo(() => {
    const q = (query || "").toLowerCase().trim();
    if (!q) return sourcePlans;
    return sourcePlans.filter((p) => {
      const tax = p.taxable ? "taxable" : "nontaxable";
      return (
        String(p.name).toLowerCase().includes(q) ||
        String(p.frequency).toLowerCase().includes(q) ||
        String(p.amount).includes(q) ||
        tax.includes(q)
      );
    });
  }, [sourcePlans, query]);

  function handleRowClick(plan) {
    onApply?.(plan);
    onClose?.();
  }

  const currency = (n) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(n || 0));

  return (
    <Dialog open={!!onOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {t("select_payment_plans")}
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          {t("selecting_a_plan_fills_info_in_payment_modal")}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1.5 }}>
        <Box sx={{ mb: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            label={t("search")}
            placeholder={t("type_to_filter")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="payment plans table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t("name")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t("amount")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t("frequency")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t("taxable")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => (
                <TableRow
                  key={plan.id}
                  hover
                  onClick={() => handleRowClick(plan)}
                  sx={{
                    cursor: "pointer",
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell>{plan.name}</TableCell>
                  <TableCell>{currency(plan.amount)}</TableCell>
                  <TableCell>{plan.frequency}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={plan.taxable ? t("yes") : t("no")}
                      color={plan.taxable ? "warning" : "default"}
                      variant={plan.taxable ? "filled" : "outlined"}
                    />
                  </TableCell>
                </TableRow>
              ))}

              {!plans.length && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      {plansQuery.isLoading
                        ? t("loading")
                        : t("no_items_found")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.25 }}>
        <Button color="error" onClick={onClose}>
          {t("cancel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
